import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button, Drawer } from "@opendate-app/setlist";

import { useBroadcastEditContext } from "./BroadcastEditContainer";
import { toastSuccess, toastError } from "../../../shared/toastHelper";
import { offsetDateForBrowser } from "../../../shared/timeZoneLogic";

const styles = {
  row: {
    borderBottom: "1px solid var(--sl-border-secondary)",
    padding: "12px 0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
  },
  when: {
    color: "var(--sl-text-primary)",
  },
  meta: {
    color: "var(--sl-text-tertiary)",
  },
  empty: {
    color: "var(--sl-text-tertiary)",
  },
  loadMore: {
    marginTop: 12,
  },
};

export const formatCount = (chars) => (chars >= 1000 ? `~${Math.round(chars / 1000)}K chars` : `${chars} chars`);

const formatMagnitude = (chars) => (chars >= 1000 ? `${(chars / 1000).toFixed(1)}K` : `${chars}`);

const formatDelta = (delta) => `${delta > 0 ? "+" : "−"}${formatMagnitude(Math.abs(delta))}`;

const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

// ponytail: relative time compares the true instant to now, so it stays on the
// raw ISO. offsetDateForBrowser relabels the instant to venue wall-clock and is
// only for the absolute tooltip below.
export const relativeTime = (iso) => {
  const diffSec = Math.round((new Date(iso).getTime() - Date.now()) / 1000);
  const abs = Math.abs(diffSec);
  if (abs < 60) return rtf.format(diffSec, "second");
  if (abs < 3600) return rtf.format(Math.round(diffSec / 60), "minute");
  if (abs < 86400) return rtf.format(Math.round(diffSec / 3600), "hour");
  return rtf.format(Math.round(diffSec / 86400), "day");
};

export const useTemplateVersions = () => {
  const { team, broadcast, csrfToken, setProcessedTemplate, cancelAutosave } = useBroadcastEditContext();
  const templateId = broadcast.beefree_template?.id;

  const [versions, setVersions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  const load = (nextPage) => {
    setLoading(true);
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.get(`/teams/${team.id}/beefree_templates/${templateId}/versions.json`, {
      params: { page: nextPage },
    })
      .then(({ data }) => {
        setTotal(data.total);
        setVersions((prev) => (nextPage === 1 ? data.versions : [...prev, ...data.versions]));
        setHasLoaded(true);
      })
      .catch(() => toastError("Could not load version history, please try again"))
      .finally(() => setLoading(false));
  };

  const restore = (version) => {
    if (!window.confirm("Restore this version? Your current content will be replaced (this is itself undoable).")) return;

    cancelAutosave();
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.post(`/teams/${team.id}/beefree_templates/${templateId}/versions/${version.id}/restore`)
      .then(({ data }) => {
        setProcessedTemplate(data.template);
        toastSuccess("Version restored");
        setPage(1);
        load(1);
      })
      .catch((error) => {
        const errors = error.response?.data?.errors || ["Could not restore version, please try again"];
        toastError(errors.join(", "));
      });
  };

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    load(next);
  };

  return { templateId, versions, total, loading, hasLoaded, load, loadMore, restore };
};

const VersionHistoryPanel = ({ onClose }) => {
  const { templateId, versions, total, loading, hasLoaded, load, loadMore, restore } = useTemplateVersions();

  useEffect(() => {
    if (templateId) load(1);
  }, [templateId]);

  return (
    <Drawer open onClose={onClose} title="Version history" size="sm">
      {hasLoaded && versions.length === 0 && !loading && <div style={styles.empty}>No previous versions yet.</div>}

      {versions.map((version, index) => {
        const prior = versions[index + 1];
        const delta = prior ? version.char_count - prior.char_count : 0;
        return (
          <div key={version.id} style={styles.row}>
            <div>
              <div style={styles.when} title={offsetDateForBrowser(version.created_at).toLocaleString()}>{relativeTime(version.created_at)}</div>
              <small style={styles.meta}>
                {formatCount(version.char_count)}
                {delta !== 0 && (
                  <span style={{ color: delta < 0 ? "var(--sl-text-error)" : "var(--sl-text-tertiary)" }}>
                    {" "}({formatDelta(delta)})
                  </span>
                )}
                {version.whodunnit_name ? ` · ${version.whodunnit_name}` : ""}
              </small>
            </div>
            <Button size="sm" onClick={() => restore(version)}>
              Restore
            </Button>
          </div>
        );
      })}

      {versions.length < total && (
        <div style={styles.loadMore}>
          <Button variant="link-color" disabled={loading} onClick={loadMore}>
            Load more
          </Button>
        </div>
      )}
    </Drawer>
  );
};

export default VersionHistoryPanel;
